import { createContext, useContext, useEffect, useRef } from 'react';
import { fontMask } from './studySign';

/*
 * A closed book's cover, tooled in gilt like a leather binding: a diamond-framed symbol for the book's area
 * and its name in gold blackletter, stamped into the leather (a dark lip below each gold stroke, a bright
 * edge above). Drawn at the book sprite's own pixel size and scaled up crisp.
 */

/** Which area's symbol the cover carries (set by the study for each book) */
export const CoverMark = createContext<string>('readiness');

const GLYPHS: Record<string, string[]> = {
  // Resources: a key
  resources: [
    '....#####....', '...#.....#...', '...#..#..#...', '...#.....#...', '....#####....', '......#......', '......#......',
    '......####...', '......#......', '......###....', '......#......', '......####...', '......#......',
  ],
  // Readiness: the sword in the stone
  readiness: [
    '......#......', '.....###.....', '......#......', '..#########..', '......#......', '.....#.#.....', '.....#.#.....',
    '.....#.#.....', '.....#.#.....', '.....###.....', '...#######...', '..#########..', '.###########.',
  ],
  // Prescription: the scales
  rx: [
    '......#......', '.....###.....', '#############', '.#....#....#.', '.#....#....#.', '#.#...#...#.#', '#.#...#...#.#',
    '#####.#.#####', '......#......', '......#......', '......#......', '....#####....', '...#######...',
  ],
  // Live Trading: candlesticks
  tracker: [
    '..#..........', '..#.......#..', '.###......#..', '.###.....###.', '.###..#..###.', '..#...#..###.', '..#..###.###.',
    '.....###..#..', '.....###..#..', '......#...#..', '......#......', '.............', '#############',
  ],
  // Marathon: a laurel wreath
  marathon: [
    '...#.....#...', '..##.....##..', '.#.#.....#.#.', '.##.......##.', '#.#.......#.#', '.##.......##.', '#.#.......#.#',
    '.##.......##.', '..##.....##..', '...##...##...', '....##.##....', '.....###.....', '....#...#....',
  ],
  // Mentor: an owl
  mentor: [
    '.##.......##.', '.###.....###.', '.###########.', '##..##.##..##', '#.##.#.#.##.#', '#.##.#.#.##.#', '##..##.##..##',
    '.####.#.####.', '..####.####..', '..#.#####.#..', '..#.#.#.#.#..', '...#######...', '....#...#....',
  ],
};

// Area of the cover drawn here (book sprite pixels) and the gold
const AREA = { x: 196, y: 84, w: 104, h: 78 };
// Aged gilt as candlelight shows it: bright where the light falls from the upper left, fading in clean
// bands toward the lower right, a bright edge on each stroke's top and a dark lip below
const RAMP = [[232, 200, 118], [204, 160, 74], [176, 128, 52], [140, 98, 40], [112, 78, 36]];

/** The flat cover's face spans x 186–310; as it swings the face narrows toward the spine and the gilt narrows with it */
const FLAT = [186, 310];

export function GiltCover({ title, k, face = [186, 310] }: { title: string; k: number; face?: [number, number] }) {
  const mark = useContext(CoverMark);
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let alive = true;
    document.fonts.load('500 13px "Grenze Gotisch"').then(() => {
      const x = ref.current?.getContext('2d'); if (!alive || !x) return;
      x.clearRect(0, 0, AREA.w, AREA.h);
      const on = new Set<string>();
      const set = (X: number, Y: number) => on.add(`${X},${Y}`);
      // Tooled diamond frame around the symbol, with studs at its points
      const cx = 52, cy = 26, r = 15;
      for (let t = 0; t <= r; t++) { set(cx - r + t, cy - t); set(cx + r - t, cy - t); set(cx - r + t, cy + t); set(cx + r - t, cy + t); }
      for (const [dx, dy] of [[0, -r - 2], [0, r + 2], [-r - 2, 0], [r + 2, 0]]) set(cx + dx, cy + dy);
      // The area's symbol, centred in the diamond
      const g = GLYPHS[mark] ?? GLYPHS.readiness;
      g.forEach((row, j) => [...row].forEach((ch, i) => { if (ch === '#') set(cx - 6 + i, cy - 6 + j); }));
      // The name in blackletter beneath
      const m = fontMask(title, '500 13px "Grenze Gotisch"', 130, 0.5);
      const tx = Math.round(cx - m.w / 2), ty = 50;
      for (let Y = 0; Y < m.h; Y++) for (let X = 0; X < m.w; X++) if (m.on(X, Y)) set(tx + X, ty + Y);
      // Stamp: a dark lip below and right of each stroke (pressed in), gold stroke, bright where lit from above
      const put = (X: number, Y: number, c: number[], a = 1) => { x.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${a})`; x.fillRect(X, Y, 1, 1); };
      for (const key of on) { const [X, Y] = key.split(',').map(Number); if (!on.has(`${X},${Y + 1}`)) put(X, Y + 1, [20, 10, 8], 0.55); }
      for (const key of on) {
        const [X, Y] = key.split(',').map(Number);
        // Light falloff from the upper left, stepped into bands like painted pixel shading
        // The light catches each cover's tooling at its own place
        const at = (([...title].reduce((a, ch) => a * 31 + ch.charCodeAt(0), 7) % 997) / 997) * 60 + 22;
        const fall = Math.min(1, Math.hypot(X - at, (Y - 20) * 0.8) / 70);
        let band = Math.min(3, Math.floor(fall * 4));
        if (!on.has(`${X},${Y - 1}`)) band = Math.max(0, band - 1);   // top edge catches the light
        if (!on.has(`${X},${Y + 1}`)) band = Math.min(4, band + 1);   // bottom edge in shade
        put(X, Y, RAMP[band]);
      }
    });
    return () => { alive = false; };
  }, [mark, title]);
  const sx = (face[1] - face[0]) / (FLAT[1] - FLAT[0]);
  return <canvas ref={ref} width={AREA.w} height={AREA.h} style={{ position: 'absolute', left: (face[0] + (AREA.x - FLAT[0]) * sx) * k, top: AREA.y * k, width: AREA.w * k * sx, height: AREA.h * k, imageRendering: 'pixelated', pointerEvents: 'none' }} />;
}
